/**
 * Keep the booking's flight number only while it still fits the trip.
 *
 * The date & time step and the location form used to drop the flight on ANY date or
 * pickup change, so a passenger who nudged the date (or re-picked the same airport) lost
 * a flight that was still perfectly valid. Now each change re-checks instead:
 *
 *   • date change     → look the flight up on the new date; remove it only if the
 *                       provider answers that there is no such flight that day.
 *   • location change → remove it only if the new pickup / drop-off airport no longer
 *                       matches the flight (or the trip isn't an airport trip any more).
 *
 * A provider outage (`unavailable`) never removes a flight — it proves nothing about
 * the flight, same rule as the recommendation chooser.
 *
 * Every removal records `{number, reason}` under FLIGHT_REMOVED_NOTICE; orderDetails
 * shows it in its update banner and clears it.
 */
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {isNearAirport} from "@/utils/airports";
import {flightService} from "@/services/flightService";

const upper = (v) => String(v || "").trim().toUpperCase();

// 'YYYY-MM-DD' of a flight leg's scheduled local time, e.g. "2026-10-16 19:10-04:00".
const legDate = (fd, side) => String(fd?.[side]?.scheduledTime?.local || "").slice(0, 10) || null;

const shiftYMD = (ymd, days) => {
    const [y, m, d] = ymd.split("-").map(Number);
    const dt = new Date(y, m - 1, d + days);
    const pad = (n) => String(n).padStart(2, "0");
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
};

const prettyDate = (ymd) => {
    const [y, m, d] = ymd.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString("en-US", {weekday: "short", month: "short", day: "numeric"});
};

/** Drop the flight from the booking and leave a notice saying why. */
export function removeFlight(reason) {
    const number = upper(storage.get(STORAGE_KEYS.FLIGHT_NUMBER));
    storage.remove(STORAGE_KEYS.FLIGHT_NUMBER);
    storage.remove(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS);
    storage.remove(STORAGE_KEYS.SMART_TIME_SELECTED);
    if (number) storage.set(STORAGE_KEYS.FLIGHT_REMOVED_NOTICE, {number, reason});
}

/** The pending removal notice (`{number, reason}`) — read once, then cleared. */
export function takeFlightRemovedNotice() {
    const notice = storage.get(STORAGE_KEYS.FLIGHT_REMOVED_NOTICE, null);
    if (notice) storage.remove(STORAGE_KEYS.FLIGHT_REMOVED_NOTICE);
    return notice;
}

const airportCode = (point) => {
    const a = point?.lat && point?.lng ? isNearAirport(point.lat, point.lng) : null;
    return a?.code ? upper(a.code) : null;
};

/**
 * Location change. Removes the flight only when the new destinations no longer fit it.
 * Returns true when it removed one.
 */
export function revalidateFlightForLocations(destinations) {
    if (!storage.get(STORAGE_KEYS.FLIGHT_NUMBER)) return false;
    // Mid-edit (an address being retyped) one end has no coordinates yet — judge only a
    // complete pickup + drop-off pair.
    const hasCoords = (p) => !!(p?.lat && p?.lng);
    if (!hasCoords(destinations?.[0]) || !hasCoords(destinations?.[1])) return false;

    const pickupCode = airportCode(destinations?.[0]);
    const dropoffCode = airportCode(destinations?.[1]);
    if (!pickupCode && !dropoffCode) {
        removeFlight("your ride is no longer to or from an airport");
        return true;
    }

    // Flight accepted during a provider outage: only the number is known, so there is
    // no airport to compare against — keep it while the trip is still an airport trip.
    const fd = storage.get(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS, null);
    if (!fd) return false;

    const arrival = upper(fd?.arrival?.airport?.iata);
    const departure = upper(fd?.departure?.airport?.iata);
    if (pickupCode) {
        if (arrival && arrival !== pickupCode) {
            removeFlight(`it doesn't land at your pickup airport (${pickupCode})`);
            return true;
        }
    } else if (departure && departure !== dropoffCode) {
        removeFlight(`it doesn't depart from your drop-off airport (${dropoffCode})`);
        return true;
    }
    return false;
}

/**
 * Date change. Looks the flight up on the new ride date and keeps it (with fresh
 * details) when it operates then; removes it only when the provider says it doesn't.
 * `rideYMD` is 'YYYY-MM-DD'. Resolves to true when it removed one.
 */
export async function revalidateFlightForDate(rideYMD, isFromAirport) {
    const number = String(storage.get(STORAGE_KEYS.FLIGHT_NUMBER) || "").trim();
    if (!number || !rideYMD) return false;

    const side = isFromAirport ? "arrival" : "departure";
    const fd = storage.get(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS, null);

    // A pickup can fall on the day after a late landing (fromAirport), or the day before
    // an early departure (toAirport), so the neighbouring day counts as a match too.
    const candidates = [rideYMD, shiftYMD(rideYMD, isFromAirport ? -1 : 1)];
    if (fd && candidates.includes(legDate(fd, side))) return false;

    const role = isFromAirport ? "Arrival" : "Departure";
    for (const ymd of candidates) {
        const result = await flightService.getFlightDetails(number, ymd, role);
        if (result.unavailable) return false; // outage — can't tell, keep it
        const found = result.success ? result.data?.[0] : null;
        if (found) {
            storage.set(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS, found);
            return false;
        }
    }

    removeFlight(`it doesn't operate on ${prettyDate(rideYMD)}`);
    return true;
}
